// =============================================================================
// Fundraising Service (Phase II)
// =============================================================================
// API client for CRM and fundraising functionality
// =============================================================================

import api from './api';
import type {
  Contact,
  ContactWithSubscriptions,
  ContactActivity,
  CreateContactInput,
  ContactListResponse,
  ContactFilters,
  PipelineStats,
  PipelineColumn,
  PipelineStage,
  ContactActivityType,
  EmailCampaign,
  CreateCampaignInput,
  UpdateCampaignInput,
  CampaignListResponse,
  CampaignRecipient,
  CampaignStats,
  CampaignPreview,
  CampaignStatus,
} from '../types/fundraising';

// =============================================================================
// Contact Management
// =============================================================================

/**
 * List contacts with filters and pagination
 */
export async function listContacts(
  filters?: ContactFilters,
  page: number = 1,
  limit: number = 50,
  sortBy: string = 'createdAt',
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<ContactListResponse> {
  const params = new URLSearchParams();

  if (filters?.search) params.append('search', filters.search);
  if (filters?.pipelineStage) {
    if (Array.isArray(filters.pipelineStage)) {
      filters.pipelineStage.forEach((s) => params.append('pipelineStage', s));
    } else {
      params.append('pipelineStage', filters.pipelineStage);
    }
  }
  if (filters?.leadSource) {
    if (Array.isArray(filters.leadSource)) {
      filters.leadSource.forEach((s) => params.append('leadSource', s));
    } else {
      params.append('leadSource', filters.leadSource);
    }
  }
  if (filters?.tags) {
    filters.tags.forEach((t) => params.append('tags', t));
  }
  if (filters?.hasPortalAccess !== undefined) {
    params.append('hasPortalAccess', String(filters.hasPortalAccess));
  }
  if (filters?.emailUnsubscribed !== undefined) {
    params.append('emailUnsubscribed', String(filters.emailUnsubscribed));
  }

  params.append('page', String(page));
  params.append('limit', String(limit));
  params.append('sortBy', sortBy);
  params.append('sortOrder', sortOrder);

  const response = await api.get<ContactListResponse>(
    `/fundraising/contacts?${params.toString()}`
  );
  return response.data;
}

/**
 * Get contact by ID
 */
export async function getContact(id: string): Promise<ContactWithSubscriptions> {
  const response = await api.get<ContactWithSubscriptions>(`/fundraising/contacts/${id}`);
  return response.data;
}

/**
 * Create a new contact
 */
export async function createContact(data: CreateContactInput): Promise<Contact> {
  const response = await api.post<Contact>('/fundraising/contacts', data);
  return response.data;
}

/**
 * Update a contact
 */
export async function updateContact(
  id: string,
  data: Partial<CreateContactInput>
): Promise<Contact> {
  const response = await api.put<Contact>(`/fundraising/contacts/${id}`, data);
  return response.data;
}

/**
 * Delete a contact
 */
export async function deleteContact(id: string): Promise<void> {
  await api.delete(`/fundraising/contacts/${id}`);
}

// =============================================================================
// Pipeline Management
// =============================================================================

/**
 * Change contact's pipeline stage
 */
export async function changePipelineStage(
  contactId: string,
  stage: PipelineStage
): Promise<Contact> {
  const response = await api.put<Contact>(`/fundraising/contacts/${contactId}/stage`, {
    stage,
  });
  return response.data;
}

/**
 * Get pipeline statistics
 */
export async function getPipelineStats(): Promise<PipelineStats[]> {
  const response = await api.get<PipelineStats[]>('/fundraising/pipeline/stats');
  return response.data;
}

/**
 * Get pipeline view (contacts grouped by stage)
 */
export async function getPipelineView(): Promise<PipelineColumn[]> {
  const response = await api.get<PipelineColumn[]>('/fundraising/pipeline');
  return response.data;
}

// =============================================================================
// Tags Management
// =============================================================================

/**
 * Update contact tags
 */
export async function updateTags(contactId: string, tags: string[]): Promise<Contact> {
  const response = await api.put<Contact>(`/fundraising/contacts/${contactId}/tags`, {
    tags,
  });
  return response.data;
}

/**
 * Get all tags used in the system
 */
export async function getAllTags(): Promise<string[]> {
  const response = await api.get<string[]>('/fundraising/tags');
  return response.data;
}

// =============================================================================
// Activity Logging
// =============================================================================

/**
 * Get contact activities
 */
export async function getContactActivities(
  contactId: string,
  limit: number = 50
): Promise<ContactActivity[]> {
  const response = await api.get<ContactActivity[]>(
    `/fundraising/contacts/${contactId}/activities?limit=${limit}`
  );
  return response.data;
}

/**
 * Log a new activity for a contact
 */
export async function logActivity(
  contactId: string,
  type: ContactActivityType,
  description: string,
  metadata?: Record<string, any>
): Promise<ContactActivity> {
  const response = await api.post<ContactActivity>(
    `/fundraising/contacts/${contactId}/activities`,
    { type, description, metadata }
  );
  return response.data;
}

// =============================================================================
// Bulk Operations
// =============================================================================

/**
 * Bulk import contacts
 */
export async function bulkImportContacts(
  contacts: CreateContactInput[]
): Promise<{ imported: number; skipped: number; errors: string[] }> {
  const response = await api.post<{ imported: number; skipped: number; errors: string[] }>(
    '/fundraising/contacts/import',
    { contacts }
  );
  return response.data;
}

// =============================================================================
// Data Room Access
// =============================================================================

import type {
  DataRoomAccess,
  ContactDataRoomAccess,
  GrantAccessInput,
  AvailableDeal,
} from '../types/fundraising';

/**
 * Grant data room access to a contact
 */
export async function grantDataRoomAccess(data: GrantAccessInput): Promise<DataRoomAccess> {
  const response = await api.post<DataRoomAccess>('/dataroom/access', data);
  return response.data;
}

/**
 * Revoke data room access
 */
export async function revokeDataRoomAccess(
  accessId: string,
  reason?: string
): Promise<DataRoomAccess> {
  const response = await api.delete<DataRoomAccess>(`/dataroom/access/${accessId}`, {
    data: { reason },
  });
  return response.data;
}

/**
 * Get data room access by ID
 */
export async function getDataRoomAccess(accessId: string): Promise<DataRoomAccess> {
  const response = await api.get<DataRoomAccess>(`/dataroom/access/${accessId}`);
  return response.data;
}

/**
 * Get all data room access for a contact
 */
export async function getContactDataRoomAccess(
  contactId: string
): Promise<ContactDataRoomAccess[]> {
  const response = await api.get<ContactDataRoomAccess[]>(
    `/dataroom/contacts/${contactId}/access`
  );
  return response.data;
}

/**
 * Get all contacts with access to a deal's data room
 */
export async function getDealDataRoomAccess(dealId: string): Promise<DataRoomAccess[]> {
  const response = await api.get<DataRoomAccess[]>(`/dataroom/deals/${dealId}/access`);
  return response.data;
}

/**
 * Check if contact has active access to a deal
 */
export async function checkDataRoomAccess(
  contactId: string,
  dealId: string
): Promise<{ hasAccess: boolean }> {
  const response = await api.get<{ hasAccess: boolean }>(
    `/dataroom/contacts/${contactId}/deals/${dealId}/check`
  );
  return response.data;
}

/**
 * Get available deals for data room access
 */
export async function getAvailableDeals(): Promise<AvailableDeal[]> {
  const response = await api.get<AvailableDeal[]>('/dataroom/deals/available');
  return response.data;
}

/**
 * Mark NDA as signed
 */
export async function markNdaSigned(
  accessId: string,
  ndaDocumentPath?: string,
  ndaSignatureId?: string
): Promise<DataRoomAccess> {
  const response = await api.post<DataRoomAccess>(`/dataroom/access/${accessId}/nda-signed`, {
    ndaDocumentPath,
    ndaSignatureId,
  });
  return response.data;
}

// =============================================================================
// Subscription Management
// =============================================================================

import type {
  SubscriptionWithDetails,
  CreateSubscriptionInput,
  UpdateSubscriptionInput,
  SubscriptionStats,
  SubscriptionStatus,
} from '../types/fundraising';

/**
 * Create a new subscription
 */
export async function createSubscription(
  data: CreateSubscriptionInput
): Promise<SubscriptionWithDetails> {
  const response = await api.post<SubscriptionWithDetails>('/subscriptions', data);
  return response.data;
}

/**
 * Get subscription by ID
 */
export async function getSubscription(subscriptionId: string): Promise<SubscriptionWithDetails> {
  const response = await api.get<SubscriptionWithDetails>(`/subscriptions/${subscriptionId}`);
  return response.data;
}

/**
 * Get all subscriptions for a contact
 */
export async function getContactSubscriptions(
  contactId: string
): Promise<SubscriptionWithDetails[]> {
  const response = await api.get<SubscriptionWithDetails[]>(`/subscriptions/contact/${contactId}`);
  return response.data;
}

/**
 * Get all subscriptions for a deal
 */
export async function getDealSubscriptions(dealId: string): Promise<SubscriptionWithDetails[]> {
  const response = await api.get<SubscriptionWithDetails[]>(`/subscriptions/deal/${dealId}`);
  return response.data;
}

/**
 * Update a subscription
 */
export async function updateSubscription(
  subscriptionId: string,
  data: UpdateSubscriptionInput
): Promise<SubscriptionWithDetails> {
  const response = await api.put<SubscriptionWithDetails>(`/subscriptions/${subscriptionId}`, data);
  return response.data;
}

/**
 * Change subscription status
 */
export async function changeSubscriptionStatus(
  subscriptionId: string,
  status: SubscriptionStatus
): Promise<SubscriptionWithDetails> {
  const response = await api.put<SubscriptionWithDetails>(
    `/subscriptions/${subscriptionId}/status`,
    { status }
  );
  return response.data;
}

/**
 * Record funding for a subscription
 */
export async function recordSubscriptionFunding(
  subscriptionId: string,
  amount: number,
  reference?: string
): Promise<SubscriptionWithDetails> {
  const response = await api.post<SubscriptionWithDetails>(
    `/subscriptions/${subscriptionId}/funding`,
    { amount, reference }
  );
  return response.data;
}

/**
 * Cancel a subscription
 */
export async function cancelSubscription(
  subscriptionId: string,
  reason?: string
): Promise<SubscriptionWithDetails> {
  const response = await api.post<SubscriptionWithDetails>(
    `/subscriptions/${subscriptionId}/cancel`,
    { reason }
  );
  return response.data;
}

/**
 * Get subscription statistics for a deal
 */
export async function getDealSubscriptionStats(dealId: string): Promise<SubscriptionStats> {
  const response = await api.get<SubscriptionStats>(`/subscriptions/deal/${dealId}/stats`);
  return response.data;
}

// =============================================================================
// Email Campaign Management
// =============================================================================

/**
 * Create a new email campaign
 */
export async function createCampaign(data: CreateCampaignInput): Promise<EmailCampaign> {
  const response = await api.post('/campaigns', data);
  return response.data.data || response.data;
}

/**
 * Get campaign by ID
 */
export async function getCampaign(campaignId: string): Promise<EmailCampaign> {
  const response = await api.get(`/campaigns/${campaignId}`);
  return response.data.data || response.data;
}

/**
 * List campaigns with pagination
 */
export async function listCampaigns(
  page: number = 1,
  limit: number = 20,
  status?: CampaignStatus
): Promise<CampaignListResponse> {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('limit', String(limit));
  if (status) params.append('status', status);

  const response = await api.get(`/campaigns?${params.toString()}`);
  const body = response.data;
  return {
    campaigns: body.data || [],
    pagination: body.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
  } as CampaignListResponse;
}

/**
 * Update a campaign
 */
export async function updateCampaign(
  campaignId: string,
  data: UpdateCampaignInput
): Promise<EmailCampaign> {
  const response = await api.put(`/campaigns/${campaignId}`, data);
  return response.data.data || response.data;
}

/**
 * Delete a campaign
 */
export async function deleteCampaign(campaignId: string): Promise<void> {
  await api.delete(`/campaigns/${campaignId}`);
}

/**
 * Preview campaign recipients
 */
export async function previewCampaignRecipients(campaignId: string): Promise<CampaignPreview> {
  const response = await api.get(`/campaigns/${campaignId}/preview`);
  return response.data.data || response.data;
}

/**
 * Send campaign
 */
export async function sendCampaign(campaignId: string): Promise<EmailCampaign> {
  const response = await api.post(`/campaigns/${campaignId}/send`);
  return response.data.data || response.data;
}

/**
 * Duplicate campaign
 */
export async function duplicateCampaign(campaignId: string): Promise<EmailCampaign> {
  const response = await api.post(`/campaigns/${campaignId}/duplicate`);
  return response.data.data || response.data;
}

/**
 * Get campaign statistics
 */
export async function getCampaignStats(campaignId: string): Promise<CampaignStats> {
  const response = await api.get(`/campaigns/${campaignId}/stats`);
  return response.data.data || response.data;
}

/**
 * Get campaign recipients
 */
export async function getCampaignRecipients(
  campaignId: string,
  page: number = 1,
  limit: number = 50
): Promise<{ recipients: CampaignRecipient[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const response = await api.get(`/campaigns/${campaignId}/recipients?page=${page}&limit=${limit}`);
  const body = response.data;
  return {
    recipients: body.data || [],
    pagination: body.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
  };
}

// =============================================================================
// Export Service
// =============================================================================

export const fundraisingService = {
  // Contacts
  listContacts,
  getContact,
  createContact,
  updateContact,
  deleteContact,

  // Pipeline
  changePipelineStage,
  getPipelineStats,
  getPipelineView,

  // Tags
  updateTags,
  getAllTags,

  // Activities
  getContactActivities,
  logActivity,

  // Bulk
  bulkImportContacts,

  // Data Room
  grantDataRoomAccess,
  revokeDataRoomAccess,
  getDataRoomAccess,
  getContactDataRoomAccess,
  getDealDataRoomAccess,
  checkDataRoomAccess,
  getAvailableDeals,
  markNdaSigned,

  // Subscriptions
  createSubscription,
  getSubscription,
  getContactSubscriptions,
  getDealSubscriptions,
  updateSubscription,
  changeSubscriptionStatus,
  recordSubscriptionFunding,
  cancelSubscription,
  getDealSubscriptionStats,

  // Campaigns
  createCampaign,
  getCampaign,
  listCampaigns,
  updateCampaign,
  deleteCampaign,
  previewCampaignRecipients,
  sendCampaign,
  duplicateCampaign,
  getCampaignStats,
  getCampaignRecipients,

  // Conversion
  getReadyForConversion,
  checkCanConvert,
  convertSubscription,
  bulkConvertSubscriptions,

  // Prospect Portal
  getProspectDataRooms,
  getProspectDataRoomDocuments,
  downloadProspectDocument,
  recordProspectDocumentView,
  getProspectNdaStatus,
  signProspectNda,

  // Portal Access
  grantPortalAccess,
  revokePortalAccess,
};

// =============================================================================
// Conversion Service (Subscription → Investor)
// =============================================================================

import type {
  ConversionResult,
  ConversionInput,
  BulkConversionResult,
  CanConvertResult,
  ReadyForConversionItem,
} from '../types/fundraising';

/**
 * Get subscriptions ready for conversion
 */
export async function getReadyForConversion(dealId?: string): Promise<ReadyForConversionItem[]> {
  const params = dealId ? `?dealId=${dealId}` : '';
  const response = await api.get(`/conversion/ready${params}`);
  return response.data.data || [];
}

/**
 * Check if a subscription can be converted
 */
export async function checkCanConvert(subscriptionId: string): Promise<CanConvertResult> {
  const response = await api.get(`/conversion/check/${subscriptionId}`);
  return response.data.data || response.data;
}

/**
 * Convert a subscription to investor
 */
export async function convertSubscription(data: ConversionInput): Promise<ConversionResult> {
  const response = await api.post('/conversion/convert', data);
  return response.data.data || response.data;
}

/**
 * Bulk convert subscriptions to investors
 */
export async function bulkConvertSubscriptions(
  subscriptionIds: string[],
  sendWelcomeEmails: boolean = true
): Promise<BulkConversionResult> {
  const response = await api.post('/conversion/bulk', {
    subscriptionIds,
    sendWelcomeEmails,
  });
  return response.data.data || response.data;
}

// =============================================================================
// Prospect Portal Service
// =============================================================================

import type {
  ProspectDataRoom,
  ProspectDataRoomDocument,
  ProspectNdaStatus,
  SignNdaInput,
} from '../types/fundraising';

/**
 * Get all data rooms the prospect has access to
 */
export async function getProspectDataRooms(): Promise<ProspectDataRoom[]> {
  const response = await api.get('/prospect/datarooms');
  return response.data.data || response.data;
}

/**
 * Get documents for a specific data room
 */
export async function getProspectDataRoomDocuments(
  dealId: string
): Promise<ProspectDataRoomDocument[]> {
  const response = await api.get(
    `/prospect/datarooms/${dealId}/documents`
  );
  return response.data.data || response.data;
}

/**
 * Download a document from a data room
 */
export async function downloadProspectDocument(
  dealId: string,
  documentId: string
): Promise<{ filePath: string; fileName: string }> {
  const response = await api.get(
    `/prospect/datarooms/${dealId}/documents/${documentId}/download`
  );
  return response.data.data || response.data;
}

/**
 * Record document view
 */
export async function recordProspectDocumentView(
  dealId: string,
  documentId: string
): Promise<void> {
  await api.post(`/prospect/datarooms/${dealId}/documents/${documentId}/view`);
}

/**
 * Get NDA status for a data room
 */
export async function getProspectNdaStatus(dealId: string): Promise<ProspectNdaStatus> {
  const response = await api.get(`/prospect/datarooms/${dealId}/nda`);
  return response.data.data || response.data;
}

/**
 * Sign NDA for a data room
 */
export async function signProspectNda(
  dealId: string,
  data: SignNdaInput
): Promise<{ signedAt: string; signatureRequestId: string }> {
  const response = await api.post(
    `/prospect/datarooms/${dealId}/nda/sign`,
    data
  );
  return response.data.data || response.data;
}

// =============================================================================
// Portal Access Management
// =============================================================================

export interface GrantPortalAccessInput {
  sendWelcomeEmail?: boolean;
  dealIds?: string[];
}

export interface GrantPortalAccessResult {
  success: boolean;
  userId?: string;
  tempPassword?: string;
  message: string;
}

/**
 * Grant portal access to a contact (creates PROSPECT user)
 */
export async function grantPortalAccess(
  contactId: string,
  options: GrantPortalAccessInput = {}
): Promise<GrantPortalAccessResult> {
  const response = await api.post<GrantPortalAccessResult>(
    `/fundraising/contacts/${contactId}/portal-access`,
    options
  );
  return response.data;
}

/**
 * Revoke portal access from a contact
 */
export async function revokePortalAccess(
  contactId: string
): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<{ success: boolean; message: string }>(
    `/fundraising/contacts/${contactId}/portal-access`
  );
  return response.data;
}

export default fundraisingService;
